/**
 * Native (Capacitor) bootstrap.
 *
 * Initialises status bar, splash screen, and push notification
 * registration when the React app is running inside the Capacitor
 * iOS / Android shell. On the web (browser) every call short-circuits
 * — `Capacitor.isNativePlatform()` returns false, so this file is a
 * complete no-op outside the wrapped app.
 *
 * Push-token persistence:
 *   On register, we POST the device token to
 *   `${REACT_APP_BACKEND_URL}/api/native/register-push-token` so the
 *   backend can target this device. The Authorization header is
 *   pulled from localStorage `access_token` (set by AuthPage on
 *   sign-in). If the user isn't logged in yet we skip — App.js calls
 *   `initNative()` again right after a successful login.
 */
import axios from "axios";

let _initialized = false;

async function safeImport(name) {
  try {
    // dynamic import so the web build doesn't fail when Capacitor
    // isn't installed in dev (it always is now, but guard anyway).
    return await import(/* webpackIgnore: false */ name);
  } catch (e) {
    return null;
  }
}

export async function isNative() {
  const core = await safeImport("@capacitor/core");
  return Boolean(core?.Capacitor?.isNativePlatform?.());
}

export async function initNative({ accessToken } = {}) {
  if (_initialized) return;
  const core = await safeImport("@capacitor/core");
  if (!core?.Capacitor?.isNativePlatform?.()) return; // web — bail out
  _initialized = true;

  // Status bar — dark style on the black-themed app.
  try {
    const sb = await safeImport("@capacitor/status-bar");
    if (sb?.StatusBar) {
      await sb.StatusBar.setStyle({ style: sb.Style?.Dark || "DARK" });
      try {
        await sb.StatusBar.setBackgroundColor({ color: "#000000" });
      } catch (_) {
        /* iOS doesn't support setBackgroundColor — ignore */
      }
    }
  } catch (_) {
    /* non-fatal */
  }

  // Auto-hide splash (config also handles this; belt-and-braces).
  try {
    const ss = await safeImport("@capacitor/splash-screen");
    if (ss?.SplashScreen) {
      setTimeout(() => ss.SplashScreen.hide().catch(() => {}), 1500);
    }
  } catch (_) {
    /* non-fatal */
  }

  // Push notifications — request permission, register, capture token.
  try {
    const push = await safeImport("@capacitor/push-notifications");
    const PushNotifications = push?.PushNotifications;
    if (!PushNotifications) return;

    const perm = await PushNotifications.requestPermissions();
    if (perm?.receive !== "granted") return;
    await PushNotifications.register();

    PushNotifications.addListener("registration", async (token) => {
      try {
        const t = accessToken || localStorage.getItem("access_token");
        if (!t) return; // user not logged in yet
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/native/register-push-token`,
          {
            token: token?.value,
            platform: core.Capacitor.getPlatform?.() || "unknown",
          },
          { headers: { Authorization: `Bearer ${t}` } }
        );
      } catch (e) {
        // never crash native shell on token-upload failures
        console.warn("[native] push token upload failed:", e?.message || e);
      }
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.warn("[native] push registration error:", err);
    });

    PushNotifications.addListener("pushNotificationReceived", (notif) => {
      // Foreground delivery — App.js can subscribe via window event.
      try {
        window.dispatchEvent(
          new CustomEvent("native:push", { detail: notif })
        );
      } catch (_) {}
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (act) => {
      try {
        window.dispatchEvent(
          new CustomEvent("native:push:tap", { detail: act })
        );
      } catch (_) {}
    });
  } catch (e) {
    console.warn("[native] push init failed:", e?.message || e);
  }
}
